import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PDFDocument } from 'pdf-lib';

/**
 * PDF Field Mapper Tool
 * A hidden admin tool for manually mapping form fields to PDF locations.
 * Access via: setPage('pdf-mapper')
 */

// Predefined field types that can be mapped
const FIELD_TYPES = [
  // Party Information
  { id: 'sellerName', label: 'Seller Name', category: 'Parties' },
  { id: 'sellerAddress', label: 'Seller Address', category: 'Parties' },
  { id: 'buyerName', label: 'Buyer Name', category: 'Parties' },
  { id: 'buyerAddress', label: 'Buyer Address', category: 'Parties' },
  { id: 'grantorName', label: 'Grantor Name', category: 'Parties' },
  { id: 'grantorAddress', label: 'Grantor Address', category: 'Parties' },
  { id: 'granteeName', label: 'Grantee Name', category: 'Parties' },
  { id: 'granteeAddress', label: 'Grantee Address', category: 'Parties' },
  { id: 'borrowerName', label: 'Borrower Name', category: 'Parties' },
  { id: 'borrowerAddress', label: 'Borrower Address', category: 'Parties' },
  { id: 'lenderName', label: 'Lender Name', category: 'Parties' },
  { id: 'lenderAddress', label: 'Lender Address', category: 'Parties' },
  { id: 'mortgagorName', label: 'Mortgagor Name', category: 'Parties' },
  { id: 'mortgageeName', label: 'Mortgagee Name', category: 'Parties' },
  { id: 'affiantName', label: 'Affiant Name', category: 'Parties' },
  { id: 'affiantAddress', label: 'Affiant Address', category: 'Parties' },
  { id: 'partyName1', label: 'Party 1 Name', category: 'Parties' },
  { id: 'partyName2', label: 'Party 2 Name', category: 'Parties' },
  { id: 'partyAddress1', label: 'Party 1 Address', category: 'Parties' },
  { id: 'partyAddress2', label: 'Party 2 Address', category: 'Parties' },

  // Property Information
  { id: 'propertyAddress', label: 'Property Address', category: 'Property' },
  { id: 'city', label: 'City', category: 'Property' },
  { id: 'county', label: 'County', category: 'Property' },
  { id: 'state', label: 'State', category: 'Property' },
  { id: 'zipCode', label: 'ZIP Code', category: 'Property' },
  { id: 'block', label: 'Block', category: 'Property' },
  { id: 'lot', label: 'Lot', category: 'Property' },
  { id: 'section', label: 'Section', category: 'Property' },
  { id: 'district', label: 'District', category: 'Property' },

  // Financial
  { id: 'purchasePrice', label: 'Purchase Price', category: 'Financial' },
  { id: 'consideration', label: 'Consideration', category: 'Financial' },
  { id: 'downPayment', label: 'Down Payment', category: 'Financial' },
  { id: 'mortgageAmount', label: 'Mortgage Amount', category: 'Financial' },
  { id: 'originalAmount', label: 'Original Amount', category: 'Financial' },
  { id: 'taxAmount', label: 'Tax Amount', category: 'Financial' },

  // Dates
  { id: 'date', label: 'Date', category: 'Dates' },
  { id: 'signatureDate', label: 'Signature Date', category: 'Dates' },
  { id: 'closingDate', label: 'Closing Date', category: 'Dates' },
  { id: 'contractDate', label: 'Contract Date', category: 'Dates' },
  { id: 'executionDate', label: 'Execution Date', category: 'Dates' },
  { id: 'recordingDate', label: 'Recording Date', category: 'Dates' },
  { id: 'mortgageDate', label: 'Mortgage Date', category: 'Dates' },
  { id: 'dateDay', label: 'Day (the ___ day of)', category: 'Dates' },
  { id: 'dateMonth', label: 'Month (of ___)', category: 'Dates' },
  { id: 'dateYear', label: 'Year', category: 'Dates' },
  { id: 'dateOfDeath', label: 'Date of Death', category: 'Dates' },

  // Recording Info
  { id: 'recordedLiber', label: 'Liber/Book', category: 'Recording' },
  { id: 'recordedPage', label: 'Page', category: 'Recording' },
  { id: 'documentNumber', label: 'Document Number', category: 'Recording' },
  { id: 'crfn', label: 'CRFN', category: 'Recording' },

  // Miscellaneous
  { id: 'notaryName', label: 'Notary Name', category: 'Misc' },
  { id: 'witnessName', label: 'Witness Name', category: 'Misc' },
  { id: 'ssn', label: 'SSN / EIN / Tax ID', category: 'Misc' },
  { id: 'decedentName', label: 'Decedent Name', category: 'Misc' },
  { id: 'notes', label: 'Notes/Comments', category: 'Misc' },
  { id: 'custom1', label: 'Custom Field 1', category: 'Custom' },
  { id: 'custom2', label: 'Custom Field 2', category: 'Custom' },
  { id: 'custom3', label: 'Custom Field 3', category: 'Custom' },
  { id: 'custom4', label: 'Custom Field 4', category: 'Custom' },
  { id: 'custom5', label: 'Custom Field 5', category: 'Custom' },
];

// Group fields by category
const FIELDS_BY_CATEGORY = FIELD_TYPES.reduce((acc, field) => {
  if (!acc[field.category]) acc[field.category] = [];
  acc[field.category].push(field);
  return acc;
}, {});

export default function PDFFieldMapper() {
  // Form selection state
  const [formsManifest, setFormsManifest] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);

  // PDF viewing state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [pdfFields, setPdfFields] = useState([]);

  // Mapping state
  const [mappings, setMappings] = useState({});
  const [selectedFieldType, setSelectedFieldType] = useState('');
  const [clickedPoints, setClickedPoints] = useState([]);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedPdfField, setSelectedPdfField] = useState('');
  const [pdfFieldMappings, setPdfFieldMappings] = useState({}); // Maps UI field -> PDF field name

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [allMappings, setAllMappings] = useState({});

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Load forms manifest and auto-generated mappings on mount
  useEffect(() => {
    fetch('/forms/forms_manifest.json')
      .then(res => res.json())
      .then(data => setFormsManifest(data))
      .catch(err => console.error('Failed to load manifest:', err));

    // Load auto-generated mappings first, then override with localStorage saves
    import('./autoGeneratedMappings.json')
      .then(autoMappings => {
        let mappings = { ...autoMappings.default };

        // Override with any saved mappings from localStorage
        const saved = localStorage.getItem('pdfFieldMappings');
        if (saved) {
          try {
            const savedMappings = JSON.parse(saved);
            mappings = { ...mappings, ...savedMappings };
          } catch (e) {
            console.error('Failed to parse saved mappings:', e);
          }
        }

        setAllMappings(mappings);
        console.log(`Loaded ${Object.keys(mappings).length} form mappings`);
      })
      .catch(err => {
        console.log('No auto-generated mappings found, using localStorage only');
        const saved = localStorage.getItem('pdfFieldMappings');
        if (saved) {
          try {
            setAllMappings(JSON.parse(saved));
          } catch (e) {
            console.error('Failed to parse saved mappings:', e);
          }
        }
      });
  }, []);

  // Load PDF when form is selected
  useEffect(() => {
    if (!selectedForm || !selectedCategory) return;

    const loadPdf = async () => {
      setLoading(true);
      setError('');
      setPdfFields([]);
      setClickedPoints([]);

      try {
        const pdfPath = `/forms/${selectedCategory}/${selectedForm.filename}`;
        const response = await fetch(pdfPath);
        if (!response.ok) throw new Error(`Failed to load PDF: ${response.status}`);

        const pdfBytes = await response.arrayBuffer();
        const doc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });

        setPdfDoc(doc);
        setTotalPages(doc.getPageCount());
        setCurrentPage(1);

        // Try to get existing form fields
        try {
          const form = doc.getForm();
          const fields = form.getFields();
          const fieldInfo = fields.map(f => ({
            name: f.getName(),
            type: f.constructor.name,
          }));
          setPdfFields(fieldInfo);
        } catch (e) {
          console.log('No form fields or could not read them:', e.message);
          setPdfFields([]);
        }

        // Load existing mappings for this form
        const formKey = `${selectedCategory}/${selectedForm.name}`;
        if (allMappings[formKey]) {
          setMappings(allMappings[formKey].fieldMappings || {});
          setPdfFieldMappings(allMappings[formKey].pdfFieldMappings || {});
          setClickedPoints(allMappings[formKey].clickPoints || []);
        } else {
          setMappings({});
          setPdfFieldMappings({});
          setClickedPoints([]);
        }
        setSelectedPdfField('');

      } catch (err) {
        setError(err.message);
        console.error('Error loading PDF:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPdf();
  }, [selectedForm, selectedCategory, allMappings]);

  // Render PDF page to canvas
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      const page = pdfDoc.getPage(currentPage - 1);
      const { width, height } = page.getSize();

      const canvas = canvasRef.current;
      canvas.width = width * scale;
      canvas.height = height * scale;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // We can't directly render PDF to canvas with pdf-lib
      // So we'll use an iframe or object tag to display the PDF
      // and overlay our click points on top
    };

    renderPage();
  }, [pdfDoc, currentPage, scale]);

  // Handle click on PDF to mark a field location
  const handleCanvasClick = useCallback((e) => {
    if (!selectedFieldType) {
      alert('Please select a field type first');
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100; // percentage
    const y = ((e.clientY - rect.top) / rect.height) * 100; // percentage

    const newPoint = {
      id: Date.now(),
      x,
      y,
      page: currentPage,
      fieldType: selectedFieldType,
      fieldLabel: FIELD_TYPES.find(f => f.id === selectedFieldType)?.label || selectedFieldType,
    };

    setClickedPoints(prev => [...prev, newPoint]);

    // Add to mappings
    setMappings(prev => ({
      ...prev,
      [selectedFieldType]: {
        ...(prev[selectedFieldType] || {}),
        x,
        y,
        page: currentPage,
      }
    }));
  }, [selectedFieldType, currentPage]);

  // Remove a clicked point
  const removePoint = useCallback((pointId) => {
    setClickedPoints(prev => {
      const point = prev.find(p => p.id === pointId);
      if (point) {
        // Also remove from mappings
        setMappings(m => {
          const newMappings = { ...m };
          delete newMappings[point.fieldType];
          return newMappings;
        });
      }
      return prev.filter(p => p.id !== pointId);
    });
  }, []);

  // Save current form's mappings
  const saveMappings = useCallback(() => {
    if (!selectedForm || !selectedCategory) return;

    const formKey = `${selectedCategory}/${selectedForm.name}`;
    const newAllMappings = {
      ...allMappings,
      [formKey]: {
        formName: selectedForm.name,
        category: selectedCategory,
        filename: selectedForm.filename,
        fieldMappings: mappings,
        pdfFieldMappings: pdfFieldMappings,
        clickPoints: clickedPoints,
        lastModified: new Date().toISOString(),
      }
    };

    setAllMappings(newAllMappings);
    localStorage.setItem('pdfFieldMappings', JSON.stringify(newAllMappings));
    alert('Mappings saved!');
  }, [selectedForm, selectedCategory, mappings, pdfFieldMappings, clickedPoints, allMappings]);

  // Export mappings as JSON
  const exportMappings = useCallback(() => {
    const dataStr = JSON.stringify(allMappings, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pdf-field-mappings.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [allMappings]);

  // Export as pdfFieldMappings.js format (compatible with existing system)
  const exportAsCode = useCallback(() => {
    let code = '// Auto-generated PDF Field Mappings\n';
    code += '// Generated: ' + new Date().toISOString() + '\n';
    code += '// This file can be merged with src/pdfFieldMappings.js\n\n';

    // Export PDF field name mappings (the main export format)
    code += 'export const PDF_FIELD_MAPPINGS = {\n';
    Object.entries(allMappings).forEach(([formKey, formData]) => {
      if (formData.pdfFieldMappings && Object.keys(formData.pdfFieldMappings).length > 0) {
        code += `  '${formData.formName}': {\n`;
        Object.entries(formData.pdfFieldMappings).forEach(([uiFieldId, pdfFieldNames]) => {
          const fieldNamesStr = pdfFieldNames.map(n => `'${n}'`).join(', ');
          code += `    '${uiFieldId}': [${fieldNamesStr}],\n`;
        });
        code += '  },\n\n';
      }
    });
    code += '};\n\n';

    // Also export coordinate mappings for forms without form fields
    code += '// Coordinate-based mappings (for PDFs without form fields)\n';
    code += 'export const PDF_COORDINATE_MAPPINGS = {\n';
    Object.entries(allMappings).forEach(([formKey, formData]) => {
      if (formData.fieldMappings && Object.keys(formData.fieldMappings).length > 0) {
        code += `  '${formData.formName}': {\n`;
        Object.entries(formData.fieldMappings).forEach(([fieldId, coords]) => {
          code += `    '${fieldId}': { x: ${coords.x.toFixed(2)}, y: ${coords.y.toFixed(2)}, page: ${coords.page} },\n`;
        });
        code += '  },\n\n';
      }
    });
    code += '};\n';

    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pdfFieldMappings_generated.js';
    a.click();
    URL.revokeObjectURL(url);
  }, [allMappings]);

  // Import mappings from JSON
  const importMappings = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        setAllMappings(prev => ({ ...prev, ...imported }));
        localStorage.setItem('pdfFieldMappings', JSON.stringify({ ...allMappings, ...imported }));
        alert('Mappings imported successfully!');
      } catch (err) {
        alert('Failed to import: ' + err.message);
      }
    };
    reader.readAsText(file);
  }, [allMappings]);

  const categories = Object.keys(formsManifest);

  return (
    <div style={styles.container}>
      <style>{`
        .mapper-sidebar::-webkit-scrollbar { width: 6px; }
        .mapper-sidebar::-webkit-scrollbar-track { background: #f1f1f1; }
        .mapper-sidebar::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
        .mapper-sidebar::-webkit-scrollbar-thumb:hover { background: #555; }
        .field-btn:hover { background: #e8e4d9 !important; }
        .field-btn.selected { background: #a08b5b !important; color: white !important; }
        .point-marker { cursor: pointer; transition: transform 0.2s; }
        .point-marker:hover { transform: scale(1.2); }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>PDF Field Mapper</h1>
        <p style={styles.subtitle}>Click on PDF locations to map form fields</p>
        <div style={styles.headerActions}>
          <button onClick={saveMappings} style={styles.saveBtn} disabled={!selectedForm}>
            Save Mappings
          </button>
          <button onClick={() => setShowExport(!showExport)} style={styles.exportBtn}>
            Export/Import
          </button>
        </div>
      </div>

      {/* Export/Import Panel */}
      {showExport && (
        <div style={styles.exportPanel}>
          <button onClick={exportMappings} style={styles.actionBtn}>Export as JSON</button>
          <button onClick={exportAsCode} style={styles.actionBtn}>Export as JS Code</button>
          <label style={styles.importLabel}>
            Import JSON
            <input type="file" accept=".json" onChange={importMappings} style={{ display: 'none' }} />
          </label>
          <span style={styles.mappingCount}>
            {Object.keys(allMappings).length} forms mapped
          </span>
        </div>
      )}

      <div style={styles.mainLayout}>
        {/* Left Sidebar - Form Selection */}
        <div style={styles.sidebar} className="mapper-sidebar">
          <h3 style={styles.sidebarTitle}>Select Form</h3>

          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedForm(null);
            }}
            style={styles.select}
          >
            <option value="">-- Select Category --</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
            ))}
          </select>

          {selectedCategory && (
            <div style={styles.formList}>
              {(formsManifest[selectedCategory] || []).map((form, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedForm(form)}
                  style={{
                    ...styles.formItem,
                    background: selectedForm?.filename === form.filename ? '#a08b5b' : 'white',
                    color: selectedForm?.filename === form.filename ? 'white' : '#333',
                  }}
                >
                  {form.name}
                  {allMappings[`${selectedCategory}/${form.name}`] && (
                    <span style={styles.mappedBadge}>✓</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Existing PDF Fields */}
          {pdfFields.length > 0 && (
            <div style={styles.pdfFieldsSection}>
              <h4 style={styles.sidebarSubtitle}>Existing PDF Fields ({pdfFields.length})</h4>
              <p style={{ fontSize: '0.75rem', color: '#8a8a8a', marginBottom: '0.5rem' }}>
                Click a PDF field, then select a UI field to map them
              </p>
              <div style={styles.pdfFieldsList}>
                {pdfFields.map((field, idx) => (
                  <button
                    key={idx}
                    style={{
                      ...styles.pdfFieldItem,
                      cursor: 'pointer',
                      border: selectedPdfField === field.name ? '2px solid #a08b5b' : '1px solid #f0ede8',
                      background: selectedPdfField === field.name ? '#f8f5f0' : 'transparent',
                      width: '100%',
                      textAlign: 'left',
                    }}
                    onClick={() => setSelectedPdfField(field.name)}
                  >
                    <span style={styles.fieldName}>{field.name}</span>
                    <span style={styles.fieldType}>{field.type.replace('PDF', '')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center - PDF Viewer */}
        <div style={styles.pdfContainer} ref={containerRef}>
          {loading && <div style={styles.loading}>Loading PDF...</div>}
          {error && <div style={styles.error}>{error}</div>}

          {selectedForm && !loading && !error && (
            <>
              {/* Page Controls */}
              <div style={styles.pageControls}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  style={styles.pageBtn}
                >
                  ← Prev
                </button>
                <span style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  style={styles.pageBtn}
                >
                  Next →
                </button>
                <select
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                  style={styles.scaleSelect}
                >
                  <option value="0.75">75%</option>
                  <option value="1">100%</option>
                  <option value="1.25">125%</option>
                  <option value="1.5">150%</option>
                  <option value="2">200%</option>
                </select>
              </div>

              {/* PDF Display with Click Overlay */}
              <div style={styles.pdfWrapper}>
                <div
                  style={styles.pdfClickArea}
                  onClick={handleCanvasClick}
                >
                  <object
                    data={`/forms/${selectedCategory}/${selectedForm.filename}#page=${currentPage}`}
                    type="application/pdf"
                    style={{
                      width: '100%',
                      height: '800px',
                      pointerEvents: 'none',
                    }}
                  >
                    <p>PDF cannot be displayed</p>
                  </object>

                  {/* Render click points for current page */}
                  {clickedPoints
                    .filter(p => p.page === currentPage)
                    .map(point => (
                      <div
                        key={point.id}
                        className="point-marker"
                        style={{
                          position: 'absolute',
                          left: `${point.x}%`,
                          top: `${point.y}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: 10,
                        }}
                        onMouseEnter={() => setHoveredPoint(point.id)}
                        onMouseLeave={() => setHoveredPoint(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Remove "${point.fieldLabel}"?`)) {
                            removePoint(point.id);
                          }
                        }}
                      >
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#a08b5b',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}>
                          {clickedPoints.filter(p => p.page === currentPage).indexOf(point) + 1}
                        </div>
                        {hoveredPoint === point.id && (
                          <div style={styles.tooltip}>
                            {point.fieldLabel}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}

          {!selectedForm && (
            <div style={styles.placeholder}>
              <p>Select a form from the left sidebar to begin mapping fields</p>
            </div>
          )}
        </div>

        {/* Right Sidebar - Field Selection */}
        <div style={styles.rightSidebar} className="mapper-sidebar">
          <h3 style={styles.sidebarTitle}>Select Field to Map</h3>
          <p style={styles.instructions}>
            1. Select a field type below<br/>
            2. Click on the PDF where this field should be filled
          </p>

          {Object.entries(FIELDS_BY_CATEGORY).map(([category, fields]) => (
            <div key={category} style={styles.fieldCategory}>
              <h4 style={styles.categoryTitle}>{category}</h4>
              {fields.map(field => (
                <button
                  key={field.id}
                  className={`field-btn ${selectedFieldType === field.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedFieldType(field.id);
                    // If a PDF field is selected, map it to this UI field
                    if (selectedPdfField) {
                      setPdfFieldMappings(prev => ({
                        ...prev,
                        [field.id]: [...(prev[field.id] || []), selectedPdfField]
                      }));
                      setSelectedPdfField('');
                    }
                  }}
                  style={{
                    ...styles.fieldBtn,
                    background: selectedFieldType === field.id ? '#a08b5b' : 'white',
                    color: selectedFieldType === field.id ? 'white' : '#333',
                    borderLeft: (mappings[field.id] || pdfFieldMappings[field.id]) ? '3px solid #28a745' : '3px solid transparent',
                  }}
                >
                  <span>{field.label}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {pdfFieldMappings[field.id] && (
                      <span style={{ fontSize: '0.65rem', color: '#666', fontFamily: 'monospace' }}>
                        [{pdfFieldMappings[field.id].join(', ')}]
                      </span>
                    )}
                    {(mappings[field.id] || pdfFieldMappings[field.id]) && <span style={styles.checkmark}>✓</span>}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {/* PDF Field Mappings */}
          {Object.keys(pdfFieldMappings).length > 0 && (
            <div style={styles.currentMappings}>
              <h4 style={styles.categoryTitle}>PDF Field Mappings</h4>
              {Object.entries(pdfFieldMappings).map(([uiField, pdfFields]) => (
                <div key={uiField} style={styles.mappingItem}>
                  <span style={{ fontSize: '0.75rem' }}>
                    {FIELD_TYPES.find(f => f.id === uiField)?.label || uiField}
                  </span>
                  <span style={{ ...styles.mappingCoords, fontFamily: 'monospace' }}>
                    → {pdfFields.join(', ')}
                  </span>
                  <button
                    onClick={() => {
                      setPdfFieldMappings(prev => {
                        const newMappings = { ...prev };
                        delete newMappings[uiField];
                        return newMappings;
                      });
                    }}
                    style={styles.removeBtn}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Click Point Mappings */}
          {clickedPoints.length > 0 && (
            <div style={styles.currentMappings}>
              <h4 style={styles.categoryTitle}>Coordinate Mappings ({clickedPoints.length})</h4>
              {clickedPoints.map(point => (
                <div key={point.id} style={styles.mappingItem}>
                  <span>{point.fieldLabel}</span>
                  <span style={styles.mappingCoords}>
                    p{point.page} ({point.x.toFixed(1)}%, {point.y.toFixed(1)}%)
                  </span>
                  <button
                    onClick={() => removePoint(point.id)}
                    style={styles.removeBtn}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f3f0',
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  header: {
    background: 'white',
    padding: '1.5rem 2rem',
    borderBottom: '1px solid #e0dcd4',
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    flexWrap: 'wrap',
  },
  title: {
    fontFamily: '"Playfair Display", Georgia, serif',
    fontSize: '1.75rem',
    margin: 0,
    color: '#0f0f0f',
  },
  subtitle: {
    margin: 0,
    color: '#5a5a5a',
    fontSize: '0.9rem',
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '0.75rem',
  },
  saveBtn: {
    background: '#a08b5b',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.875rem',
  },
  exportBtn: {
    background: '#0f0f0f',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.875rem',
  },
  exportPanel: {
    background: 'white',
    padding: '1rem 2rem',
    borderBottom: '1px solid #e0dcd4',
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionBtn: {
    background: '#f5f3f0',
    border: '1px solid #e0dcd4',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  importLabel: {
    background: '#f5f3f0',
    border: '1px solid #e0dcd4',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  mappingCount: {
    marginLeft: 'auto',
    color: '#5a5a5a',
    fontSize: '0.875rem',
  },
  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr 300px',
    gap: '0',
    height: 'calc(100vh - 120px)',
  },
  sidebar: {
    background: 'white',
    borderRight: '1px solid #e0dcd4',
    padding: '1.5rem',
    overflowY: 'auto',
  },
  rightSidebar: {
    background: 'white',
    borderLeft: '1px solid #e0dcd4',
    padding: '1.5rem',
    overflowY: 'auto',
  },
  sidebarTitle: {
    fontFamily: '"Playfair Display", Georgia, serif',
    fontSize: '1.125rem',
    marginTop: 0,
    marginBottom: '1rem',
    color: '#0f0f0f',
  },
  sidebarSubtitle: {
    fontSize: '0.875rem',
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    color: '#5a5a5a',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #e0dcd4',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    background: 'white',
  },
  formList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  formItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.625rem 0.75rem',
    border: '1px solid #e0dcd4',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  mappedBadge: {
    color: '#28a745',
    fontWeight: 'bold',
  },
  pdfFieldsSection: {
    marginTop: '1.5rem',
    borderTop: '1px solid #e0dcd4',
    paddingTop: '1rem',
  },
  pdfFieldsList: {
    maxHeight: '200px',
    overflowY: 'auto',
  },
  pdfFieldItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.375rem 0',
    fontSize: '0.75rem',
    borderBottom: '1px solid #f0ede8',
  },
  fieldName: {
    color: '#0f0f0f',
    fontFamily: 'monospace',
  },
  fieldType: {
    color: '#8a8a8a',
    fontSize: '0.6875rem',
  },
  pdfContainer: {
    background: '#e8e4d9',
    overflow: 'auto',
    position: 'relative',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#5a5a5a',
    fontSize: '1rem',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#dc3545',
    fontSize: '1rem',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#5a5a5a',
    fontSize: '1rem',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1rem',
    background: 'white',
    borderBottom: '1px solid #e0dcd4',
  },
  pageBtn: {
    background: '#0f0f0f',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#5a5a5a',
  },
  scaleSelect: {
    padding: '0.5rem',
    border: '1px solid #e0dcd4',
    fontSize: '0.875rem',
    marginLeft: '1rem',
  },
  pdfWrapper: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
  pdfClickArea: {
    position: 'relative',
    cursor: 'crosshair',
    background: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    maxWidth: '100%',
    width: '800px',
  },
  tooltip: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#0f0f0f',
    color: 'white',
    padding: '0.375rem 0.625rem',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    marginTop: '4px',
    borderRadius: '2px',
    zIndex: 20,
  },
  instructions: {
    fontSize: '0.8125rem',
    color: '#5a5a5a',
    lineHeight: 1.6,
    marginBottom: '1rem',
    padding: '0.75rem',
    background: '#f8f7f5',
    border: '1px solid #e0dcd4',
  },
  fieldCategory: {
    marginBottom: '1rem',
  },
  categoryTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#8a8a8a',
    marginBottom: '0.5rem',
    marginTop: '0',
  },
  fieldBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #e0dcd4',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    textAlign: 'left',
    marginBottom: '0.25rem',
    transition: 'all 0.2s',
  },
  checkmark: {
    color: '#28a745',
    fontWeight: 'bold',
  },
  currentMappings: {
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e0dcd4',
  },
  mappingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.375rem',
    fontSize: '0.75rem',
    background: '#f8f7f5',
    marginBottom: '0.25rem',
  },
  mappingCoords: {
    color: '#8a8a8a',
    fontSize: '0.6875rem',
    marginLeft: 'auto',
  },
  removeBtn: {
    background: '#dc3545',
    color: 'white',
    border: 'none',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
};
